import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/** Frame members: axial force plus end moments. Bars: axial force only. */
const CHORD_GROUPS = new Set([
  'bottom_chord',
  'top_chord',
  'top_chord_left',
  'top_chord_right',
  'arch_rib',
  'arch_leg',
  'column_outer',
  'column_inner',
  'column',
]);

/** Bottom / inner chords: node text sits on the inward normal. */
const INWARD_GROUPS = new Set([
  'bottom_chord',
  'arch_leg',
  'column_inner',
]);

const TENSION = '#c62828';
const COMPRESSION = '#1565c0';
const NEUTRAL = '#90a4ae';

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function padTag(prefix, n) {
  const value = Math.max(0, Math.round(n));
  return `${prefix}${String(value).padStart(2, '0')}`;
}

/**
 * N is tension-positive, matching the forces table (frame rows use N at the i-end).
 * M_i / M_j are the member end moments at those nodes.
 */
function indexForces(solution) {
  const bars = new Map();
  const frames = new Map();
  (solution?.truss_forces || []).forEach((f) => {
    bars.set(`${f.i}-${f.j}`, f);
    bars.set(`${f.j}-${f.i}`, f);
  });
  (solution?.frame_forces || []).forEach((f) => {
    frames.set(`${f.i}-${f.j}`, f);
  });
  return { bars, frames };
}

function memberAction(member, index) {
  const frame = index.frames.get(`${member.i}-${member.j}`);
  if (frame) {
    return {
      chord: true,
      N: num(frame.N_i_kN),
      moments: [
        { node: member.i, M: num(frame.M_i_kNm) },
        { node: member.j, M: num(frame.M_j_kNm) },
      ],
    };
  }
  const reversed = index.frames.get(`${member.j}-${member.i}`);
  if (reversed) {
    return {
      chord: true,
      N: -num(reversed.N_j_kN),
      moments: [
        { node: member.i, M: num(reversed.M_j_kNm) },
        { node: member.j, M: num(reversed.M_i_kNm) },
      ],
    };
  }
  const bar = index.bars.get(`${member.i}-${member.j}`);
  if (bar) {
    return { chord: false, N: num(bar.N_kN), moments: [] };
  }
  return null;
}

function escapeXml(text) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;');
}

/**
 * One drawing for the card and the popup. Metres use the same scale in x and y,
 * so the arch shape does not change when the picture is enlarged.
 */
function caseMomentIndex(caseObj) {
  const map = new Map();
  (caseObj?.members || []).forEach((member) => {
    map.set(`${member.i}-${member.j}`, { Mi: num(member.M_i_kNm), Mj: num(member.M_j_kNm) });
    map.set(`${member.j}-${member.i}`, { Mi: num(member.M_j_kNm), Mj: num(member.M_i_kNm) });
  });
  return map;
}

function ForcesSvg({ geometry, solution, momentCases }) {
  const built = useMemo(() => {
    const nodes = geometry?.nodes;
    const members = geometry?.members;
    if (!nodes?.length || !members?.length || !solution) return '';

    const actions = indexForces(solution);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    nodes.forEach((nd) => {
      minX = Math.min(minX, nd.x);
      maxX = Math.max(maxX, nd.x);
      minY = Math.min(minY, nd.y);
      maxY = Math.max(maxY, nd.y);
    });
    if (!Number.isFinite(minX)) return '';

    const span = Math.max(maxX - minX, 1e-6);
    const rise = Math.max(maxY - minY, span * 0.25);
    const pad = Math.max(span, rise) * 0.2;
    const x0 = minX - pad;
    const y0 = minY - pad;
    const worldW = (maxX + pad) - x0;
    const worldH = (maxY + pad) - y0;
    const W = 1400;
    const s = W / worldW;
    const H = worldH * s;
    const px = (x) => (x - x0) * s;
    const py = (y) => (maxY + pad - y) * s;
    const cxs = px(nodes.reduce((sum, nd) => sum + nd.x, 0) / nodes.length);
    const cys = py(nodes.reduce((sum, nd) => sum + nd.y, 0) / nodes.length);

    let nMax = 1e-6;
    const rows = [];
    members.forEach((m, index) => {
      const a = memberAction(m, actions);
      const ni = nodes[m.i];
      const nj = nodes[m.j];
      if (!a || !ni || !nj) return;
      nMax = Math.max(nMax, Math.abs(a.N));
      const chord = a.chord || CHORD_GROUPS.has(m.group);
      rows.push({
        m,
        a,
        ni,
        nj,
        chord,
        tag: m.key != null && String(m.key) !== '' ? String(m.key) : padTag('M', index + 1),
        inward: INWARD_GROUPS.has(m.group),
      });
    });

    const zeroBased = nodes.some((nd) => Number(nd.id) === 0);
    const nodeTag = (nd, fallback) => {
      const id = nd?.id != null ? Number(nd.id) : fallback;
      return padTag('N', zeroBased ? id + 1 : id);
    };

    const fontMember = 8;
    const fontNode = 8;
    const numberOff = s * Math.max(span, rise) * 0.016;
    const parts = [];
    const chordSegs = [];

    rows.forEach(({ a, ni, nj }) => {
      const mag = Math.abs(a.N) / nMax;
      const width = 1.8 + 8 * mag;
      const color = a.N > 0.05 ? TENSION : a.N < -0.05 ? COMPRESSION : NEUTRAL;
      parts.push(
        `<line x1="${px(ni.x).toFixed(1)}" y1="${py(ni.y).toFixed(1)}" x2="${px(nj.x).toFixed(1)}" y2="${py(nj.y).toFixed(1)}" stroke="${color}" stroke-width="${width.toFixed(2)}" stroke-linecap="round" />`
      );
    });

    nodes.forEach((nd) => {
      parts.push(
        `<circle cx="${px(nd.x).toFixed(1)}" cy="${py(nd.y).toFixed(1)}" r="3.2" fill="#263238" />`
      );
    });

    const diagonalNodes = new Set();
    const chordTouch = new Map();
    rows.forEach(({ m, chord }) => {
      if (m.group === 'diagonal') {
        diagonalNodes.add(m.i);
        diagonalNodes.add(m.j);
      }
      if (!chord) return;
      [m.i, m.j].forEach((nodeId) => {
        const key = `${m.group}:${nodeId}`;
        chordTouch.set(key, (chordTouch.get(key) || 0) + 1);
      });
    });
    const endNodes = new Set();
    rows.forEach(({ m, chord }) => {
      if (!chord) return;
      [m.i, m.j].forEach((nodeId) => {
        if ((chordTouch.get(`${m.group}:${nodeId}`) || 0) === 1) endNodes.add(nodeId);
      });
    });
    nodes.forEach((nd, index) => {
      if (nd.role === 'column_base') endNodes.add(index);
    });

    const minMoments = caseMomentIndex(momentCases?.min_case);
    const maxMoments = caseMomentIndex(momentCases?.max_case);

    const nodeLabel = new Map();
    const textHalo = (extra) => `font-family="system-ui, sans-serif" stroke="#fff" stroke-width="3" paint-order="stroke"${extra}`;
    rows.forEach(({ a, ni, nj, m, chord, tag, inward }) => {
      const x1p = px(ni.x);
      const y1p = py(ni.y);
      const x2p = px(nj.x);
      const y2p = py(nj.y);
      const sdx = x2p - x1p;
      const sdy = y2p - y1p;
      const slen = Math.hypot(sdx, sdy) || 1;
      let nx = -sdy / slen;
      let ny = sdx / slen;
      const midX = (x1p + x2p) / 2;
      const midY = (y1p + y2p) / 2;
      if ((midX - cxs) * nx + (midY - cys) * ny < 0) {
        nx = -nx;
        ny = -ny;
      }
      if (inward) {
        nx = -nx;
        ny = -ny;
      }
      let ang = Math.atan2(sdy, sdx) * (180 / Math.PI);
      if (ang > 90) ang -= 180;
      else if (ang < -90) ang += 180;

      const fill = a.N > 0.05 ? TENSION : a.N < -0.05 ? COMPRESSION : '#455a64';
      parts.push(
        `<text x="${midX.toFixed(1)}" y="${midY.toFixed(1)}" transform="rotate(${ang.toFixed(1)} ${midX.toFixed(1)} ${midY.toFixed(1)})" text-anchor="middle" dominant-baseline="middle" font-size="${fontMember}" ${textHalo('')} fill="${fill}">${escapeXml(tag)}</text>`
      );

      if (!chord) return;
      chordSegs.push({
        group: m.group,
        i: m.i,
        j: m.j,
        x1: x1p,
        y1: y1p,
        x2: x2p,
        y2: y2p,
        nx,
        ny,
        moments: a.moments,
      });
      [m.i, m.j].forEach((nodeId) => {
        if (!diagonalNodes.has(nodeId) && !endNodes.has(nodeId)) return;
        const nd = nodes[nodeId];
        if (!nd) return;
        const prev = nodeLabel.get(nodeId);
        if (!prev) {
          nodeLabel.set(nodeId, {
            x: px(nd.x),
            y: py(nd.y),
            nx,
            ny,
            inward,
            name: nodeTag(nd, nodeId),
          });
          return;
        }
        prev.nx += nx;
        prev.ny += ny;
        if (inward) prev.inward = true;
      });
    });

    nodeLabel.forEach((label, nodeId) => {
      const len = Math.hypot(label.nx, label.ny) || 1;
      const nx = label.nx / len;
      const ny = label.ny / len;
      const lx = label.x + nx * numberOff;
      const ly = label.y + ny * numberOff;
      const numberAnchor = Math.abs(nx) >= Math.abs(ny) ? (nx < 0 ? 'end' : 'start') : 'middle';
      parts.push(
        `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" text-anchor="${numberAnchor}" dominant-baseline="middle" font-size="${fontNode}" ${textHalo('')} fill="#263238">${escapeXml(label.name)}</text>`
      );
    });

    const endsOf = (index, seg) => {
      const hit = index.get(`${seg.i}-${seg.j}`);
      if (hit) return hit;
      const mi = seg.moments.find((mom) => mom.node === seg.i);
      const mj = seg.moments.find((mom) => mom.node === seg.j);
      return { Mi: num(mi?.M), Mj: num(mj?.M) };
    };
    let mPeak = 1e-6;
    chordSegs.forEach((seg) => {
      [minMoments, maxMoments].forEach((index) => {
        const ends = endsOf(index, seg);
        mPeak = Math.max(mPeak, Math.abs(ends.Mi), Math.abs(ends.Mj));
      });
    });
    const mReach = s * Math.max(span, rise) * 0.07;
    const mScale = mReach / mPeak;
    const pointOf = new Map();
    chordSegs.forEach((seg) => {
      pointOf.set(seg.i, { x: seg.x1, y: seg.y1 });
      pointOf.set(seg.j, { x: seg.x2, y: seg.y2 });
    });

    const chains = [];
    const byGroup = new Map();
    chordSegs.forEach((seg, index) => {
      if (!byGroup.has(seg.group)) byGroup.set(seg.group, []);
      byGroup.get(seg.group).push(index);
    });
    byGroup.forEach((indexes) => {
      const adj = new Map();
      const link = (nodeId, index) => {
        if (!adj.has(nodeId)) adj.set(nodeId, []);
        adj.get(nodeId).push(index);
      };
      indexes.forEach((index) => {
        link(chordSegs[index].i, index);
        link(chordSegs[index].j, index);
      });
      const unused = new Set(indexes);
      while (unused.size) {
        let start = null;
        adj.forEach((list, nodeId) => {
          if (list.filter((index) => unused.has(index)).length === 1) start = nodeId;
        });
        if (start == null) start = chordSegs[unused.values().next().value].i;
        const ordered = [start];
        const segs = [];
        let node = start;
        while (unused.size) {
          const nextIndex = (adj.get(node) || []).find((index) => unused.has(index));
          if (nextIndex == null) break;
          unused.delete(nextIndex);
          const seg = chordSegs[nextIndex];
          segs.push(seg);
          node = seg.i === node ? seg.j : seg.i;
          ordered.push(node);
        }
        if (ordered.length > 1) chains.push({ ordered, segs });
        else unused.delete(unused.values().next().value);
      }
    });

    const shift = (x, y, nx, ny, moment) => `${(x + nx * moment * mScale).toFixed(1)},${(y + ny * moment * mScale).toFixed(1)}`;
    const normalAt = (nodeId, segs) => {
      let sxn = 0;
      let syn = 0;
      segs.forEach((seg) => {
        if (seg.i !== nodeId && seg.j !== nodeId) return;
        sxn += seg.nx;
        syn += seg.ny;
      });
      const len = Math.hypot(sxn, syn) || 1;
      return { x: sxn / len, y: syn / len };
    };
    const momentAt = (index, nodeId, segs) => {
      const vals = [];
      segs.forEach((seg) => {
        if (seg.i !== nodeId && seg.j !== nodeId) return;
        const ends = endsOf(index, seg);
        vals.push(seg.i === nodeId ? ends.Mi : -ends.Mj);
      });
      if (!vals.length) return 0;
      return vals.reduce((sum, value) => sum + value, 0) / vals.length;
    };

    const momentParts = [];
    chains.forEach(({ ordered, segs }) => {
      const edge = (index) => ordered.map((nodeId) => {
        const pt = pointOf.get(nodeId);
        if (!pt) return null;
        const n = normalAt(nodeId, segs);
        return shift(pt.x, pt.y, n.x, n.y, momentAt(index, nodeId, segs));
      }).filter(Boolean);
      const maxPts = edge(maxMoments);
      const minPts = edge(minMoments);
      if (maxPts.length < 2) return;
      const poly = maxPts.concat(minPts.slice().reverse()).join(' ');
      momentParts.push(
        `<polygon points="${poly}" fill="#6a1b9a" fill-opacity="0.18" stroke="#6a1b9a" stroke-width="1.1" stroke-linejoin="round" />`
      );
    });

    return `<svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 ${W.toFixed(1)} ${H.toFixed(1)}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Member forces">${momentParts.join('')}${parts.join('')}</svg>`;
  }, [geometry, solution, momentCases]);

  if (!built) return null;
  return (
    <Box
      sx={{ width: '100%', '& svg': { display: 'block', width: '100%', height: 'auto' } }}
      dangerouslySetInnerHTML={{ __html: built }}
    />
  );
}

function Legend() {
  const swatch = (color) => ({
    width: 22,
    height: 5,
    borderRadius: 2,
    bgcolor: color,
    display: 'inline-block',
  });
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 1, px: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={swatch(TENSION)} />
        <Typography variant="caption">Tension (N &gt; 0)</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={swatch(COMPRESSION)} />
        <Typography variant="caption">Compression (N &lt; 0)</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ ...swatch('#6a1b9a'), height: 8, opacity: 0.45 }} />
        <Typography variant="caption">Chord moment envelope</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Red is tension and blue is compression. Line thickness follows |N|. Labels on the members are the member keys. The purple band is one continuous line along each chord, for the ULS case with the smallest bottom-chord moment and the case with the largest. Node numbers sit close to a joint where a diagonal connects, and at the chord ends.
      </Typography>
    </Box>
  );
}

export default function TrussForcesFigure({ geometry, solution, momentCases }) {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const wheelCleanup = useRef(null);
  const hasForces = (solution?.truss_forces || []).length > 0 || (solution?.frame_forces || []).length > 0;

  const bindWheel = useCallback((node) => {
    if (wheelCleanup.current) {
      wheelCleanup.current();
      wheelCleanup.current = null;
    }
    if (!node) return undefined;
    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
      setZoom((current) => Math.min(8, Math.max(0.6, current * factor)));
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    wheelCleanup.current = () => node.removeEventListener('wheel', onWheel);
    return undefined;
  }, []);

  if (!geometry?.nodes?.length || !hasForces) return null;

  const close = () => {
    setOpen(false);
    setZoom(1);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        component="button"
        type="button"
        variant="outlined"
        onClick={() => setOpen(true)}
        aria-label="Enlarge member forces diagram"
        sx={{
          display: 'block',
          width: '100%',
          p: 1,
          cursor: 'zoom-in',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Member forces
        </Typography>
        <ForcesSvg geometry={geometry} solution={solution} momentCases={momentCases} />
        <Legend />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Click to open at page width. In the enlarged view, Ctrl + scroll zooms the drawing.
        </Typography>
      </Paper>
      <Dialog
        open={open}
        onClose={close}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            maxHeight: '94vh',
            m: 1,
            containerType: 'inline-size',
          },
        }}
      >
        <DialogContent ref={bindWheel} sx={{ position: 'relative', px: 0, overflow: 'auto' }}>
          <Box sx={{ px: 2, pr: 6, mb: 1 }}>
            <Typography variant="subtitle1">Member forces</Typography>
            <Typography variant="caption" color="text.secondary">
              Ctrl + scroll zooms this drawing. An SVG does not block browser zoom; the wheel is used here so the figure itself can be enlarged.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close forces diagram"
            onClick={close}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ width: `${95 * zoom}cqw`, mx: 'auto' }}>
            <ForcesSvg geometry={geometry} solution={solution} momentCases={momentCases} />
          </Box>
          <Box sx={{ px: 2 }}>
            <Legend />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
